import React from 'react';

import { Box, Skeleton, Stack, useMediaQuery } from '@mui/material';

/**
 * Whether the answer-shaped skeleton should stand in for the pending answer body.
 *
 * The instant a run starts, the process ledger (PR #9560) seeds an "Understanding the question"
 * step, so it is never empty and cannot itself gate a placeholder. The honest signal for "the
 * answer has not arrived yet" is therefore the answer content: while the live streaming assistant
 * message carries no blocks, its body is empty, so we fill it with a scaffold shaped like the
 * answer that is coming. As soon as the first content block streams in, real content replaces the
 * skeleton. Only the live streaming message qualifies: a settled answer with zero blocks (an empty
 * or errored past turn) must not sprout a permanent skeleton.
 */
export function shouldShowAnswerSkeleton(params: {
  isStreaming: boolean;
  isLastAssistant: boolean;
  blockCount: number;
}): boolean {
  const { isStreaming, isLastAssistant, blockCount } = params;
  return isStreaming && isLastAssistant && blockCount === 0;
}

/** Ghost text-bar widths, in answer reading order: a short title line then body lines of varying run. */
const TITLE_WIDTH = '38%';
const BODY_LINE_WIDTHS = ['96%', '100%', '82%'] as const;
/** Ghost "table" rows under the chart block: label column plus a value column, a couple of rows. */
const TABLE_ROW_COUNT = 3;

/**
 * Answer-shaped loading scaffold shown in the answer body before any content has streamed in. It
 * mirrors the rhythm of a real answer (a heading, a few prose lines, then a chart-and-table
 * artifact) so the wait reads as structured rather than a dead gap, and so the swap to real content
 * shifts the layout as little as possible.
 *
 * It complements the process ledger above it rather than duplicating it: the ledger narrates what
 * askRichard is doing, this shows the shape of what it is about to return. Purely decorative, so it
 * is hidden from assistive tech, which is already served by the surface's "Richard is thinking"
 * live region.
 */
export default function AnswerSkeleton({ compact = false }: { compact?: boolean }) {
  // MUI's Skeleton pulse is not reduced-motion aware on its own, so still it explicitly: under a
  // reduced-motion preference the bars render static (animation=false) instead of pulsing.
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const animation = prefersReducedMotion ? false : 'pulse';

  if (compact) {
    return (
      <Stack aria-hidden spacing={0.5} sx={{ mt: 0.5, py: 0.5 }}>
        {BODY_LINE_WIDTHS.map((width, index) => (
          <Skeleton key={index} animation={animation} variant="text" width={width} sx={{ fontSize: '0.875rem' }} />
        ))}
      </Stack>
    );
  }

  return (
    <Box aria-hidden sx={{ maxWidth: 720, mt: 0.5 }}>
      {/* Title line: taller and short, like an answer heading. */}
      <Skeleton animation={animation} variant="text" width={TITLE_WIDTH} sx={{ fontSize: '1.25rem', mb: 1 }} />

      {/* Body prose: a few lines of varying width so it reads as text, not a solid block. */}
      <Stack spacing={0.5} sx={{ mb: 2 }}>
        {BODY_LINE_WIDTHS.map((width, index) => (
          <Skeleton key={index} animation={animation} variant="text" width={width} sx={{ fontSize: '0.875rem' }} />
        ))}
      </Stack>

      {/* A chart-shaped block: the artifact most answers carry. */}
      <Skeleton animation={animation} variant="rounded" height={132} sx={{ borderRadius: '8px', mb: 1.5 }} />

      {/* A couple of table rows: a label column and a value column per row. */}
      <Stack spacing={1}>
        {Array.from({ length: TABLE_ROW_COUNT }).map((_, index) => (
          <Stack key={index} direction="row" spacing={2} alignItems="center">
            <Skeleton animation={animation} variant="text" width="46%" sx={{ fontSize: '0.875rem' }} />
            <Skeleton animation={animation} variant="text" width="22%" sx={{ fontSize: '0.875rem' }} />
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}
